import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import logger from '../../utils/logger';
import { api } from '../../services/api';
import { getImageUrl } from '../../utils/imageUrl';
import { loadArticleHtml, prepareArticleQuill } from '../../utils/articlePaste';
import './ExamManagement.css';

const API_BASE_URL = (process.env.REACT_APP_API_URL || '').replace(/\/+$/, '');

// Image upload API
const IMAGE_UPLOAD_URL = (process.env.REACT_APP_IMAGE_UPLOAD_URL || '').replace(/\/+$/, '');

const ExamManagement = ({ initialSection = 'overview', selectedLanguage = 'English' }) => {
  const { t } = useTranslation('examManagement');
  const [currentSection, setCurrentSection] = useState(initialSection);
  const [showForm, setShowForm] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editArticleId, setEditArticleId] = useState(null);
  const [articles, setArticles] = useState({ 
    ielts: [], 
    toefl: [], 
    cambridge: [], 
    'other-exams': []
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [formData, setFormData] = useState({
    title: '',
    contentType: '',
    examType: '',
    subCategory: '',
    difficulty: '',
    category: '',
    content: '',
    imageFile: null,
    imagePreview: null
  });
  const quillRef = useRef(null);
  const [useQuill, setUseQuill] = useState(false);

  // Jobs (placeholder until backend exam job tracking is wired)
  const [jobsLoading, setJobsLoading] = useState(false);
  const [jobsError, setJobsError] = useState('');
  const [jobsStatusFilter, setJobsStatusFilter] = useState('');
  const [jobs, setJobs] = useState([]);
  const [jobsArticleFilter, setJobsArticleFilter] = useState('');
  const [jobByArticleId, setJobByArticleId] = useState({});

  useEffect(() => {
    setCurrentSection(initialSection);
    // eslint-disable-next-line
  }, [initialSection]);

  const langLower = selectedLanguage ? String(selectedLanguage).trim().toLowerCase() : '';
  const isFrenchLanguage = langLower === 'french';
  const isSpanishLanguage = langLower === 'spanish';
  const isGermanLanguage = langLower === 'german';
  const isItalianLanguage = langLower === 'italian';

  // Exam types - UI tabs (English vs French vs Spanish vs German vs Italian)
  const examTypesEnglish = [
    { id: 'ielts', name: t('examTypes.ielts'), icon: 'school' },
    { id: 'toefl', name: t('examTypes.toefl'), icon: 'menu_book' },
    { id: 'cambridge', name: t('examTypes.cambridge'), icon: 'bookmark' },
    { id: 'other-exams', name: t('examTypes.otherExams'), icon: 'quiz' }
  ];
  const examTypesFrench = [
    { id: 'delf-dalf', name: 'DELF / DALF', icon: 'school' },
    { id: 'tcf', name: 'TCF', icon: 'school' },
    { id: 'tef', name: 'TEF', icon: 'school' },
    { id: 'dcl', name: 'DCL', icon: 'school' },
    { id: 'other-exams', name: t('examTypes.otherExams'), icon: 'quiz' }
  ];
  const examTypesSpanish = [
    { id: 'dele', name: 'DELE', icon: 'school' },
    { id: 'siele', name: 'SIELE', icon: 'school' },
    { id: 'celu', name: 'CELU', icon: 'school' },
    { id: 'other-exams', name: t('examTypes.otherExams'), icon: 'quiz' }
  ];
  const examTypesGerman = [
    { id: 'goethe', name: 'Goethe-Zertifikat', icon: 'school' },
    { id: 'testdaf', name: 'TestDaF', icon: 'school' },
    { id: 'telc-german', name: 'telc German', icon: 'school' },
    { id: 'other-exams', name: t('examTypes.otherExams'), icon: 'quiz' }
  ];
  const examTypesItalian = [
    { id: 'cils', name: 'CILS', icon: 'school' },
    { id: 'celi', name: 'CELI', icon: 'school' },
    { id: 'plida', name: 'PLIDA', icon: 'school' },
    { id: 'other-exams', name: t('examTypes.otherExams'), icon: 'quiz' }
  ];
  const examTypes = isFrenchLanguage ? examTypesFrench : (isSpanishLanguage ? examTypesSpanish : (isGermanLanguage ? examTypesGerman : (isItalianLanguage ? examTypesItalian : examTypesEnglish)));

  // Main exam type options for the form (English vs French vs Spanish)
  const mainExamTypesEnglish = [
    { value: 'IELTS', label: 'IELTS' },
    { value: 'TOEFL', label: 'TOEFL' },
    { value: 'CAMBRIDGE', label: 'Cambridge' },
    { value: 'OTHER_EXAMS', label: 'Other Exams' }
  ];
  const mainExamTypesFrench = [
    { value: 'DELF_DALF', label: 'DELF / DALF' },
    { value: 'TCF', label: 'TCF' },
    { value: 'TEF', label: 'TEF' },
    { value: 'DCL', label: 'DCL' },
    { value: 'OTHER_EXAMS', label: 'Other Exams' }
  ];
  const mainExamTypesSpanish = [
    { value: 'DELE', label: 'DELE' },
    { value: 'SIELE', label: 'SIELE' },
    { value: 'CELU', label: 'CELU' },
    { value: 'OTHER_EXAMS', label: 'Other Exams' }
  ];
  const mainExamTypesGerman = [
    { value: 'GOETHE_ZERTIFIKAT', label: 'Goethe-Zertifikat' },
    { value: 'TESTDAF', label: 'TestDaF' },
    { value: 'TELC_GERMAN', label: 'telc German' },
    { value: 'OTHER_EXAMS', label: 'Other Exams' }
  ];
  const mainExamTypesItalian = [
    { value: 'CILS', label: 'CILS' },
    { value: 'CELI', label: 'CELI' },
    { value: 'PLIDA', label: 'PLIDA' },
    { value: 'OTHER_EXAMS', label: 'Other Exams' }
  ];
  const mainExamTypes = isFrenchLanguage ? mainExamTypesFrench : (isSpanishLanguage ? mainExamTypesSpanish : (isGermanLanguage ? mainExamTypesGerman : (isItalianLanguage ? mainExamTypesItalian : mainExamTypesEnglish)));

  // Sub-categories based on main exam type
  const examSubCategoriesEnglish = {
    'IELTS': [
      { value: 'Academic', label: 'Academic' },
      { value: 'General Training', label: 'General Training' },
      { value: 'UKVI', label: 'UKVI' }
    ],
    'TOEFL': [
      { value: 'iBT', label: 'iBT (Internet-Based Test)' },
      { value: 'PBT', label: 'PBT (Paper-Based Test)' },
      { value: 'CBT', label: 'CBT (Computer-Based Test)' }
    ],
    'CAMBRIDGE': [
      { value: 'FCE', label: 'FCE (First Certificate)' },
      { value: 'CAE', label: 'CAE (Advanced)' },
      { value: 'CPE', label: 'CPE (Proficiency)' },
      { value: 'PET', label: 'PET (Preliminary)' },
      { value: 'KET', label: 'KET (Key)' }
    ],
    'OTHER_EXAMS': [
      { value: 'PTE Academic', label: 'PTE Academic' },
      { value: 'Duolingo English Test', label: 'Duolingo English Test' },
      { value: 'OET', label: 'OET (Occupational English Test)' },
      { value: 'TOEIC', label: 'TOEIC' },
      { value: 'BULATS', label: 'BULATS' }
    ]
  };
  const examSubCategoriesFrench = {
    'DELF_DALF': [
      { value: 'A1', label: 'A1' },
      { value: 'A2', label: 'A2' },
      { value: 'B1', label: 'B1' },
      { value: 'B2', label: 'B2' },
      { value: 'C1', label: 'C1' },
      { value: 'C2', label: 'C2' }
    ],
    'TCF': [
      { value: 'Tout Public', label: 'Tout Public' },
      { value: 'DAP', label: 'DAP' },
      { value: 'Canada', label: 'Canada' }
    ],
    'TEF': [
      { value: 'TEF Canada', label: 'TEF Canada' },
      { value: 'TEF Naturalisation', label: 'TEF Naturalisation' }
    ],
    'DCL': [
      { value: 'A1', label: 'A1' },
      { value: 'A2', label: 'A2' },
      { value: 'B1', label: 'B1' },
      { value: 'B2', label: 'B2' },
      { value: 'C1', label: 'C1' }
    ],
    'OTHER_EXAMS': [
      { value: 'Certificat Voltaire', label: 'Certificat Voltaire' },
      { value: 'Alliance Française Certificates', label: 'Alliance Française Certificates' }
    ]
  };
  const examSubCategoriesSpanish = {
    'DELE': [
      { value: 'A1', label: 'A1' },
      { value: 'A2', label: 'A2' },
      { value: 'B1', label: 'B1' },
      { value: 'B2', label: 'B2' },
      { value: 'C1', label: 'C1' },
      { value: 'C2', label: 'C2' }
    ],
    'SIELE': [
      { value: 'Digital', label: 'Digital' },
      { value: 'Score-based', label: 'Score-based' }
    ],
    'CELU': [
      { value: 'Latin America', label: 'Latin America' }
    ],
    'OTHER_EXAMS': [
      { value: 'Citizenship', label: 'Citizenship' },
      { value: 'Regional certifications', label: 'Regional certifications' }
    ]
  };
  const examSubCategoriesGerman = {
    'GOETHE_ZERTIFIKAT': [
      { value: 'A1', label: 'A1' },
      { value: 'A2', label: 'A2' },
      { value: 'B1', label: 'B1' },
      { value: 'B2', label: 'B2' },
      { value: 'C1', label: 'C1' },
      { value: 'C2', label: 'C2' }
    ],
    'TESTDAF': [
      { value: 'University focus', label: 'University focus' }
    ],
    'TELC_GERMAN': [
      { value: 'Professional', label: 'Professional' },
      { value: 'Immigration', label: 'Immigration' }
    ],
    'OTHER_EXAMS': [
      { value: 'DSH', label: 'DSH' },
      { value: 'University-specific', label: 'University-specific' }
    ]
  };
  const examSubCategoriesItalian = {
    'CILS': [
      { value: 'A1', label: 'A1' },
      { value: 'A2', label: 'A2' },
      { value: 'B1', label: 'B1' },
      { value: 'B2', label: 'B2' },
      { value: 'C1', label: 'C1' },
      { value: 'C2', label: 'C2' }
    ],
    'CELI': [
      { value: 'Academic', label: 'Academic' },
      { value: 'Professional', label: 'Professional' }
    ],
    'PLIDA': [
      { value: 'Immigration', label: 'Immigration' }
    ],
    'OTHER_EXAMS': [
      { value: 'University certifications', label: 'University certifications' },
      { value: 'Regional certifications', label: 'Regional certifications' }
    ]
  };
  const examSubCategories = isFrenchLanguage ? examSubCategoriesFrench : (isSpanishLanguage ? examSubCategoriesSpanish : (isGermanLanguage ? examSubCategoriesGerman : (isItalianLanguage ? examSubCategoriesItalian : examSubCategoriesEnglish)));

  const difficultyLevels = [
    { value: 'Beginner', label: 'Beginner' },
    { value: 'Intermediate', label: 'Intermediate' },
    { value: 'Advanced', label: 'Advanced' }
  ];

  // Thematic content categories
  const categories = [
    { value: 'Environment', label: 'Environment' },
    { value: 'Technology', label: 'Technology' },
    { value: 'Health', label: 'Health' },
    { value: 'Education', label: 'Education' },
    { value: 'Business', label: 'Business' },
    { value: 'Science', label: 'Science' },
    { value: 'History', label: 'History' },
    { value: 'Culture', label: 'Culture' },
    { value: 'Society', label: 'Society' },
    { value: 'Economics', label: 'Economics' },
    { value: 'Politics', label: 'Politics' },
    { value: 'Travel', label: 'Travel' },
    { value: 'Food', label: 'Food' },
    { value: 'Sports', label: 'Sports' },
    { value: 'Entertainment', label: 'Entertainment' },
    { value: 'Media', label: 'Media' },
    { value: 'Art', label: 'Art' },
    { value: 'Literature', label: 'Literature' },
    { value: 'Psychology', label: 'Psychology' },
    { value: 'Philosophy', label: 'Philosophy' },
    { value: 'Geography', label: 'Geography' },
    { value: 'Architecture', label: 'Architecture' },
    { value: 'Fashion', label: 'Fashion' },
    { value: 'Nature', label: 'Nature' },
    { value: 'Urban Life', label: 'Urban Life' }
  ];

  // Quill setup
  useEffect(() => {
    if (showForm && window.Quill && quillRef.current && !useQuill) {
      setUseQuill(true);
      quillRef.current.innerHTML = '';
      const quill = new window.Quill(quillRef.current, {
        theme: 'snow',
        modules: {
          toolbar: [
            ['bold', 'italic', 'underline'],
            ['link', 'blockquote', 'code-block'],
            [{ 'list': 'ordered'}, { 'list': 'bullet' }],
            [{ 'header': [1, 2, 3, false] }],
            ['clean']
          ]
        },
        placeholder: t('form.contentPlaceholder')
      });
      quill.on('text-change', () => {
        setFormData(f => ({ ...f, content: quill.root.innerHTML }));
      });
      prepareArticleQuill(quill);
    }
  }, [showForm, t, useQuill]);

  useEffect(() => {
    if (currentSection !== 'overview' && currentSection !== 'jobs') {
      fetchArticles(currentSection);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentSection, selectedLanguage]);

  // When language changes, if current tab is not in the new exam list (e.g. was IELTS, now French), switch to first tab
  useEffect(() => {
    if (currentSection !== 'overview' && currentSection !== 'jobs' && !examTypes.some(e => e.id === currentSection)) {
      const first = examTypes[0];
      if (first) setCurrentSection(first.id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedLanguage]);

  useEffect(() => {
    if (currentSection === 'jobs') {
      fetchJobs();
    }
    // eslint-disable-next-line
  }, [currentSection, jobsStatusFilter, jobsArticleFilter]);

  useEffect(() => {
    // Best-effort: keep job badges in sync for the currently selected exam type section
    if (currentSection && currentSection !== 'overview' && currentSection !== 'jobs') {
      fetchJobsForBadges(currentSection);
    } else {
      setJobByArticleId({});
    }
    // eslint-disable-next-line
  }, [currentSection]);

  async function fetchJobs() {
    setJobsLoading(true);
    setJobsError('');
    try {
      const params = new URLSearchParams();
      if (jobsStatusFilter) params.set('status', jobsStatusFilter);
      if (jobsArticleFilter) params.set('article_id', jobsArticleFilter);
      const result = await api.get(`/api/admin/exam-vocabulary-processing-jobs?${params.toString()}`);
      setJobs(result.jobs || []);
    } catch (e) {
      setJobsError(e.message || 'Failed to load jobs');
      setJobs([]);
    } finally {
      setJobsLoading(false);
    }
  }

  async function fetchJobsForBadges(examTypeId) {
    try {
      const params = new URLSearchParams();
      params.set('limit', '1000');
      if (examTypeId) params.set('exam_type', examTypeId);
      const result = await api.get(`/api/admin/exam-vocabulary-processing-jobs?${params.toString()}`);
      const rows = result.jobs || [];
      const map = {};
      rows.forEach((j) => {
        if (j?.article_id != null) map[j.article_id] = j;
      });
      setJobByArticleId(map);
    } catch (e) {
      setJobByArticleId({});
    }
  }

  async function reprocessArticle(articleId) {
    try {
      await api.post(`/api/admin/exam-vocabulary/articles/${Number(articleId)}/reprocess-words`, {});
      alert('Reprocess queued successfully');
      fetchJobs();
      fetchJobsForBadges(currentSection !== 'overview' && currentSection !== 'jobs' ? currentSection : null);
    } catch (e) {
      alert(e.message || 'Failed to queue reprocess');
    }
  }

  function openJobsForArticle(articleId) {
    setCurrentSection('jobs');
    setJobsArticleFilter(String(articleId));
  }

  // Real image upload function - Connects to image upload server
  async function uploadImageToServer(file) {
    try {
      const formData = new FormData();
      formData.append('image', file);

      const response = await fetch(`${IMAGE_UPLOAD_URL}/api/upload-image`, {
        method: 'POST',
        body: formData
      });

      const text = await response.text();
      if (!response.ok) {
        let msg = 'Failed to upload image';
        try {
          const data = JSON.parse(text);
          if (data.error) msg = data.error;
        } catch (_) {
          if (response.status === 413) msg = 'Image is too large. Maximum size is 20MB. Try a smaller image or compress it.';
        }
        throw new Error(msg);
      }

      const result = JSON.parse(text);
      return result;
    } catch (error) {
      logger.error('Image upload error:', error);
      throw error;
    }
  }

  // Fetch articles for the selected exam type (admin: use dashboard endpoint so auth works)
  async function fetchArticles(examType) {
    setLoading(true);
    setError('');
    try {
      const params = new URLSearchParams();
      if (selectedLanguage) params.set('language', selectedLanguage);
      const url = params.toString() ? `/dashboard/vocabulary?${params.toString()}` : '/dashboard/vocabulary';
      const data = await api.get(url);
      let list = Array.isArray(data) ? data : [];
      // Client-side language filter (in case backend does not filter or is old deploy)
      if (selectedLanguage && String(selectedLanguage).trim()) {
        const langNorm = String(selectedLanguage).trim().toLowerCase();
        list = list.filter(a => {
          const v = (a && a.language != null) ? String(a.language).trim().toLowerCase() : '';
          const langMatch = (v || 'english') === (langNorm || 'english');
          return langMatch;
        });
      }
      // Map section id to DB exam_type (e.g. delf-dalf -> DELF_DALF, dele -> DELE, goethe -> GOETHE_ZERTIFIKAT)
      const sectionToDbTypeEnglish = { 'ielts': 'IELTS', 'toefl': 'TOEFL', 'cambridge': 'CAMBRIDGE', 'other-exams': 'OTHER_EXAMS' };
      const sectionToDbTypeFrench = { 'delf-dalf': 'DELF_DALF', 'tcf': 'TCF', 'tef': 'TEF', 'dcl': 'DCL', 'other-exams': 'OTHER_EXAMS' };
      const sectionToDbTypeSpanish = { 'dele': 'DELE', 'siele': 'SIELE', 'celu': 'CELU', 'other-exams': 'OTHER_EXAMS' };
      const sectionToDbTypeGerman = { 'goethe': 'GOETHE_ZERTIFIKAT', 'testdaf': 'TESTDAF', 'telc-german': 'TELC_GERMAN', 'other-exams': 'OTHER_EXAMS' };
      const sectionToDbTypeItalian = { 'cils': 'CILS', 'celi': 'CELI', 'plida': 'PLIDA', 'other-exams': 'OTHER_EXAMS' };
      const sectionToDbType = isFrenchLanguage ? sectionToDbTypeFrench : (isSpanishLanguage ? sectionToDbTypeSpanish : (isGermanLanguage ? sectionToDbTypeGerman : (isItalianLanguage ? sectionToDbTypeItalian : sectionToDbTypeEnglish)));
      const dbType = sectionToDbType[examType] || examType.replace(/-/g, '_').toUpperCase();
      const mainTypes = isFrenchLanguage ? ['DELF_DALF', 'TCF', 'TEF', 'DCL'] : (isSpanishLanguage ? ['DELE', 'SIELE', 'CELU'] : (isGermanLanguage ? ['GOETHE_ZERTIFIKAT', 'TESTDAF', 'TELC_GERMAN'] : (isItalianLanguage ? ['CILS', 'CELI', 'PLIDA'] : ['IELTS', 'TOEFL', 'CAMBRIDGE'])));
      const filtered = dbType === 'OTHER_EXAMS'
        ? list.filter(a => !mainTypes.includes(String(a.exam_type || '').toUpperCase()))
        : list.filter(a => String(a.exam_type || '').toUpperCase() === dbType || String(a.exam_type || '').toUpperCase().startsWith(dbType));
      setArticles(prev => ({ ...prev, [examType]: filtered }));
      setError('');
    } catch (err) {
      logger.error('Error fetching articles:', err);
      setError(t('messages.errorFetching'));
      setArticles(prev => ({ ...prev, [examType]: [] }));
    } finally {
      setLoading(false);
    }
  }

  // Add or update article
  async function handleFormSubmit(e) {
    e.preventDefault();
    
    if (!formData.title || !formData.contentType || !formData.examType || 
        !formData.subCategory || !formData.difficulty || !formData.category || !formData.content) {
      alert(t('messages.fillAllFields'));
      return;
    }

    // Handle image upload first if there's a new image
    let imageUrl = null;
    if (formData.imageFile) {
      try {
        logger.log('🖼️ Starting image upload...', formData.imageFile.name);
        // Upload image to backend server
        const uploadResult = await uploadImageToServer(formData.imageFile);
        logger.log('📤 Image upload result:', uploadResult);
        if (uploadResult.success) {
          imageUrl = uploadResult.imageUrl;
          logger.log('✅ Image uploaded successfully:', imageUrl);
        } else {
          throw new Error('Failed to upload image');
        }
      } catch (error) {
        logger.error('❌ Image upload error:', error);
        alert(t('messages.errorUploadingImage', { message: error.message }));
        return;
      }
    } else {
      logger.log('ℹ️ No image file selected');
    }

    logger.log('📝 Creating exam article with image URL:', imageUrl);

    const quill = (window.Quill && quillRef.current) ? window.Quill.find(quillRef.current) : null;
    const sourceContent = quill?.root?.innerHTML || formData.content;

    const articleData = {
      title: formData.title,
      content: sourceContent,
      exam_type: formData.examType,
      difficulty: formData.difficulty,
      category: formData.subCategory, // Store the sub-category as the main category
      language: selectedLanguage || 'English',
      country: 'Algeria',
      teacher_id: 'admin',
      image_url: imageUrl
    };

    try {
      let res;
      if (isEditing && editArticleId) {
        res = await fetch(`${API_BASE_URL}/vocabulary/${editArticleId}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(articleData)
        });
      } else {
        res = await fetch(`${API_BASE_URL}/vocabulary`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(articleData)
        });
      }
      
      if (!res.ok) throw new Error('Failed to save article');
      
      setShowForm(false);
      setIsEditing(false);
      setEditArticleId(null);
      setFormData({ title: '', contentType: '', examType: '', subCategory: '', difficulty: '', category: '', content: '', imageFile: null, imagePreview: null });
      fetchArticles(currentSection);
      alert(isEditing ? t('messages.articleUpdated') : t('messages.articleCreated'));
    } catch (err) {
      logger.error('Error saving article:', err);
      alert(t('messages.errorSaving'));
    }
  }

  // Delete article
  async function handleDelete(article) {
    if (!window.confirm(t('messages.deleteConfirm'))) return;
    
    try {
      const res = await fetch(`${API_BASE_URL}/vocabulary/${article.id}`, { method: 'DELETE' });
      if (!res.ok) throw new Error('Failed to delete');
      fetchArticles(currentSection);
      alert(t('messages.articleDeleted'));
    } catch (err) {
      logger.error('Error deleting article:', err);
      alert(t('messages.errorDeleting'));
    }
  }

  // Edit article
  function handleEdit(article) {
    setShowForm(true);
    setIsEditing(true);
    setEditArticleId(article.id);
    setFormData({
      title: article.title,
      contentType: article.type || 'vocabulary',
      examType: article.exam_type,
      subCategory: article.category, // Load the stored category as subCategory
      difficulty: article.difficulty,
      category: article.category,
      content: article.content,
      imageFile: null,
      imagePreview: article.image_url ? (getImageUrl(article.image_url) || article.image_url) : null
    });
    setTimeout(() => {
      if (window.Quill && quillRef.current) {
        const quill = window.Quill.find(quillRef.current);
        if (quill) loadArticleHtml(quill, article.content);
      }
    }, 200);
  }

  // Form modal open/close
  function openForm() {
    setShowForm(true);
    setIsEditing(false);
    setEditArticleId(null);
    setFormData({ title: '', contentType: '', examType: '', difficulty: '', category: '', content: '', imageFile: null, imagePreview: null });
    setTimeout(() => {
      if (window.Quill && quillRef.current) {
        const quill = window.Quill.find(quillRef.current);
        if (quill) quill.root.innerHTML = '';
      }
    }, 200);
  }

  const renderOverview = () => (
    <div className="exam-overview fade-in">
      <div className="exam-header">
        <h2>{t('title')}</h2>
        <p>{t('selectExamType')}</p>
      </div>
      <div className="exam-cards">
        {examTypes.map((exam) => (
          <div 
            key={exam.id} 
            className="exam-card card-animate"
            onClick={() => setCurrentSection(exam.id)}
          >
            <div className="exam-card-header">
              <span className="material-icons">{exam.icon}</span>
              <h3>{exam.name}</h3>
            </div>
            <div className="exam-card-content">
              <p>{t(`examTypesDescription.${exam.id}`)}</p>
            </div>
            <div className="exam-card-footer">
              <span className="access-link">{t('actions.accessModule')}</span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  const renderJobs = () => (
    <div className="exam-overview fade-in">
      <div className="exam-header">
        <h2>Translation Jobs</h2>
        <p>Monitor and reprocess Exam Preparation translation preprocessing jobs.</p>
      </div>

      <div className="jobs-header">
        <h3>Jobs</h3>
        <div className="jobs-controls">
          <select value={jobsStatusFilter} onChange={(e) => setJobsStatusFilter(e.target.value)}>
            <option value="">All statuses</option>
            <option value="not_started">not_started</option>
            <option value="pending">pending</option>
            <option value="running">running</option>
            <option value="succeeded">succeeded</option>
            <option value="failed">failed</option>
          </select>
          {jobsArticleFilter ? (
            <button type="button" className="btn-secondary" onClick={() => setJobsArticleFilter('')}>
              Show all
            </button>
          ) : null}
          <button type="button" className="btn-secondary" onClick={fetchJobs}>Refresh</button>
        </div>
      </div>

      {jobsLoading ? (
        <div className="loading">Loading jobs...</div>
      ) : jobsError ? (
        <div className="empty-state">
          <span className="material-icons empty-icon">error_outline</span>
          <p>{jobsError}</p>
        </div>
      ) : jobs.length === 0 ? (
        <div className="empty-state">
          <span className="material-icons empty-icon">inbox</span>
          <p>No jobs found.</p>
        </div>
      ) : (
        <div className="jobs-table-wrapper">
          <table className="jobs-table">
            <thead>
              <tr>
                <th>Article</th>
                <th>Status</th>
                <th>Attempts</th>
                <th>Last error</th>
                <th>Started</th>
                <th>Finished</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {jobs.map((j) => (
                <tr key={j.article_id} className={jobsArticleFilter && String(j.article_id) === String(jobsArticleFilter) ? 'job-row-highlight' : ''}>
                  <td>
                    <div className="job-article-cell">
                      <div className="job-article-title">{j.article_title || `#${j.article_id}`}</div>
                      <div className="job-article-meta">
                        #{j.article_id}{j.exam_type ? ` · ${j.exam_type}` : ''}{j.difficulty ? ` · ${j.difficulty}` : ''}{j.category ? ` · ${j.category}` : ''}
                      </div>
                    </div>
                  </td>
                  <td><span className={`job-status ${j.status}`}>{j.status}</span></td>
                  <td>{j.attempts}</td>
                  <td className="job-error">{j.last_error || ''}</td>
                  <td>{j.started_at ? new Date(j.started_at).toLocaleString() : ''}</td>
                  <td>{j.finished_at ? new Date(j.finished_at).toLocaleString() : ''}</td>
                  <td>
                    <button
                      type="button"
                      className="btn-primary"
                      onClick={() => reprocessArticle(j.article_id)}
                    >
                      Reprocess
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );

  const renderExamSection = () => {
    const currentExam = examTypes.find(exam => exam.id === currentSection);
    const currentArticles = articles[currentSection] || [];
    
    return (
      <div className="exam-section fade-in">
        <div className="exam-section-header">
          <div className="header-top">
            <button 
              className="back-btn" 
              onClick={() => setCurrentSection('overview')}
              title={t('actions.cancel')}
            >
              <span className="material-icons">arrow_back</span>
              {t('actions.cancel')}
            </button>
            <h2>{currentExam?.name} Articles</h2>
          </div>
          <p>{t('messages.manageFor', { exam: currentExam?.name })}</p>
        </div>
        
        <div className="exam-controls">
          <div className="search-box">
            <input
              type="text"
              placeholder={t('messages.loadingArticles')}
              onChange={e => {
                // Optionally implement search filtering here
              }}
            />
            <span className="material-icons">search</span>
          </div>
          <button className="add-article-btn" onClick={openForm}>
            <span className="material-icons">add</span>
            {t('actions.addNewArticle')}
          </button>
        </div>

        <div className="articles-section">
          <h3>Articles for {currentExam?.name}</h3>
          {loading ? (
            <div className="loading">{t('messages.loadingArticles')}</div>
          ) : error ? (
            <div className="empty-state">
              <span className="material-icons empty-icon">error_outline</span>
              <p>{error}</p>
            </div>
          ) : currentArticles.length === 0 ? (
            <div className="empty-state">
              <span className="material-icons empty-icon">article</span>
              <p>{t('messages.noArticlesFound')}</p>
            </div>
          ) : (
            <div className="content-cards">
              {currentArticles.map((article) => (
                <div key={article.id} className="content-card card-animate">
                  {/* Article Image or Placeholder */}
                  <div className="content-card-image">
                    {article.image_url ? (
                      <img 
                        src={getImageUrl(article.image_url) || article.image_url} 
                        alt={article.title}
                        onError={(e) => {
                          e.target.style.display = 'none';
                          e.target.nextElementSibling.style.display = 'flex';
                        }}
                      />
                    ) : null}
                    {/* Image Placeholder */}
                    <div className="image-placeholder" style={{ display: article.image_url ? 'none' : 'flex' }}>
                      <div className="placeholder-content">
                        <span className="material-icons placeholder-icon">image</span>
                        <span className="placeholder-text">No Image</span>
                      </div>
                    </div>

                    <div className="content-job-badge-wrap">
                      <button
                        type="button"
                        className={`job-status ${jobByArticleId?.[article.id]?.status || 'not_started'} content-job-badge`}
                        title="Open Translation Jobs for this article"
                        onClick={() => openJobsForArticle(article.id)}
                      >
                        {jobByArticleId?.[article.id]?.status || 'not_started'}
                      </button>
                      {(jobByArticleId?.[article.id]?.status === 'failed') && (
                        <button
                          type="button"
                          className="content-job-reprocess"
                          title="Reprocess word translations"
                          onClick={() => reprocessArticle(article.id)}
                        >
                          Reprocess
                        </button>
                      )}
                    </div>
                  </div>
                  <h4 className="content-title">{article.title}</h4>
                  <div className="preview-text">
                    {article.content ? 
                      (() => {
                        const cleanContent = article.content.replace(/<[^>]*>/g, '').trim();
                        if (cleanContent.length === 0) {
                          return 'This exam preparation content covers essential topics and practice materials...';
                        }
                        const preview = cleanContent.substring(0, 80); // Reduced to 80 characters for 2 lines
                        return preview.length < cleanContent.length ? `${preview}...` : preview;
                      })() : 
                      'This exam preparation content covers essential topics and practice materials...'
                    }
                  </div>
                  <div className="card-bottom-section">
                    <div className="content-actions">
                      <button className="action-btn edit" title={t('actions.edit')} onClick={() => handleEdit(article)}>
                        <span className="material-icons">edit</span>
                        {t('actions.edit')}
                      </button>
                      <button className="action-btn delete" title={t('actions.delete')} onClick={() => handleDelete(article)}>
                        <span className="material-icons">delete</span>
                        {t('actions.delete')}
                      </button>
                    </div>
                    <div className="article-metadata">
                      <div className="metadata-item">
                        <span className="material-icons">school</span>
                        <span>{article.exam_type}</span>
                      </div>
                      <div className="metadata-item">
                        <span className="material-icons">trending_up</span>
                        <span>{article.difficulty}</span>
                      </div>
                      <div className="metadata-item">
                        <span className="material-icons">schedule</span>
                        <span>{article.created_at ? new Date(article.created_at).toLocaleDateString() : ''}</span>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    );
  };

  const renderForm = () => (
    <div className={`exam-overlay ${showForm ? 'show' : ''}`}>
      <div className="exam-form">
        <div className="form-header">
          <h3>{isEditing ? t('form.editArticle') : t('form.addNewArticle')}</h3>
          <button className="close-btn" onClick={() => setShowForm(false)}>
            <span className="material-icons">close</span>
          </button>
        </div>
        <form onSubmit={handleFormSubmit} className="form-content">
          <div className="form-group">
            <label htmlFor="article-title">{t('form.title')}:</label>
            <input 
              type="text" 
              id="article-title" 
              placeholder={t('form.titlePlaceholder')} 
              value={formData.title}
              onChange={e => setFormData(f => ({ ...f, title: e.target.value }))}
              required
            />
          </div>
          
          {/* Image Upload Field */}
          <div className="form-group">
            <label htmlFor="article-image">{t('form.image')} (Optional):</label>
            <div className="image-upload-container">
              <input 
                type="file" 
                id="article-image" 
                accept="image/*"
                onChange={e => {
                  const file = e.target.files[0];
                  if (file) {
                    setFormData(f => ({ 
                      ...f, 
                      imageFile: file,
                      imagePreview: URL.createObjectURL(file)
                    }));
                  }
                }}
                style={{ display: 'none' }}
              />
              <button 
                type="button" 
                className="image-upload-btn"
                onClick={() => document.getElementById('article-image').click()}
              >
                <span className="material-icons">add_photo_alternate</span>
                {formData.imagePreview ? t('form.uploadImage') : t('form.selectImage')}
              </button>
              
              {/* Image Preview */}
              {formData.imagePreview && (
                <div className="image-preview-container">
                  <img 
                    src={formData.imagePreview} 
                    alt="Preview" 
                    className="image-preview"
                  />
                  <button 
                    type="button" 
                    className="remove-image-btn"
                    onClick={() => setFormData(f => ({ 
                      ...f, 
                      imageFile: null, 
                      imagePreview: null 
                    }))}
                    title="Remove image"
                  >
                    <span className="material-icons">close</span>
                  </button>
                </div>
              )}
            </div>
            <p className="form-help-text">
              Upload an image to make your exam article more engaging. Recommended size: 800x600px or larger.
            </p>
          </div>
          
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="content-type">{t('form.contentType')}:</label>
              <select 
                id="content-type" 
                value={formData.contentType}
                onChange={e => setFormData(f => ({ ...f, contentType: e.target.value }))}
                required
              >
                <option value="">{t('form.selectContentType')}</option>
                <option value="vocabulary">{t('form.vocabulary')}</option>
                <option value="reading">{t('form.reading')}</option>
              </select>
            </div>
            
            <div className="form-group">
              <label htmlFor="exam-type">{t('form.examType')}:</label>
              <select 
                id="exam-type" 
                value={formData.examType}
                onChange={e => setFormData(f => ({ ...f, examType: e.target.value, subCategory: '' }))}
                required
              >
                <option value="">{t('form.selectExamType')}</option>
                {mainExamTypes.map(exam => (
                  <option key={exam.value} value={exam.value}>{exam.label}</option>
                ))}
              </select>
            </div>

            {formData.examType && (
              <div className="form-group">
                <label htmlFor="sub-category">{t('form.subCategory')}:</label>
                <select 
                  id="sub-category" 
                  value={formData.subCategory}
                  onChange={e => setFormData(f => ({ ...f, subCategory: e.target.value }))}
                  required
                >
                  <option value="">{t('form.selectSubCategory')}</option>
                  {examSubCategories[formData.examType]?.map(subCat => (
                    <option key={subCat.value} value={subCat.value}>{subCat.label}</option>
                  ))}
                </select>
              </div>
            )}
          </div>
          
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="difficulty-level">{t('form.difficulty')}:</label>
              <select 
                id="difficulty-level" 
                value={formData.difficulty}
                onChange={e => setFormData(f => ({ ...f, difficulty: e.target.value }))}
                required
              >
                <option value="">{t('form.selectDifficulty')}</option>
                {difficultyLevels.map(level => (
                  <option key={level.value} value={level.value}>{level.label}</option>
                ))}
              </select>
            </div>
            
            <div className="form-group">
              <label htmlFor="category">{t('form.category')}:</label>
              <select 
                id="category" 
                value={formData.category}
                onChange={e => setFormData(f => ({ ...f, category: e.target.value }))}
                required
              >
                <option value="">{t('form.selectCategory')}</option>
                {categories.map(category => (
                  <option key={category.value} value={category.value}>{category.label}</option>
                ))}
              </select>
            </div>
          </div>
          
          <div className="form-group">
            <label htmlFor="article-content">{t('form.content')}:</label>
            <div style={{ display: useQuill ? 'block' : 'none' }}>
              <div id="article-editor" ref={quillRef} style={{ minHeight: 200, background: '#fff', borderRadius: 8 }} />
            </div>
            <textarea
              id="fallback-editor"
              style={{ display: useQuill ? 'none' : 'block', width: '100%', minHeight: 200, padding: 12, border: '2px solid #e1e5e9', borderRadius: 8, fontSize: 16, fontFamily: 'Arial, sans-serif' }}
              placeholder={t('form.contentPlaceholder')}
              value={formData.content}
              onChange={e => setFormData(f => ({ ...f, content: e.target.value }))}
            />
          </div>
          
          <div className="form-actions">
            <button type="button" className="btn-secondary" onClick={() => setShowForm(false)}>
              {t('actions.cancel')}
            </button>
            <button type="submit" className="btn-primary">
              {isEditing ? t('actions.update') : t('actions.submit')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );

  return (
    <div className="exam-management-scroll-wrapper">
      <div className="exam-management-page">
        {/* Inner tabs (Content / Translation Jobs) */}
        <div className="vocabulary-inner-tabs">
          <button
            className={`vocabulary-inner-tab ${currentSection !== 'jobs' ? 'active' : ''}`}
            onClick={() => setCurrentSection('overview')}
          >
            <span className="material-icons">dashboard</span>
            Content
          </button>
          <button
            className={`vocabulary-inner-tab ${currentSection === 'jobs' ? 'active' : ''}`}
            onClick={() => setCurrentSection('jobs')}
          >
            <span className="material-icons">sync</span>
            Translation Jobs
          </button>
        </div>

        {currentSection === 'jobs' ? renderJobs() : (currentSection === 'overview' ? renderOverview() : renderExamSection())}
        {renderForm()}
      </div>
    </div>
  );
};

export default ExamManagement; 